import { makeStyles } from "@material-ui/core";
import { Search } from "@material-ui/icons";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { Skeleton, Tooltip } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import { cardTitle } from "assets/jss/material-dashboard-pro-react";
import Card from "components/UI/Card/Card";
import CardBody from "components/UI/Card/CardBody";
import CardHeader from "components/UI/Card/CardHeader";
import CardIcon from "components/UI/Card/CardIcon";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import GridItemTS from "components/UI/Grid/GridItemTS";
import PaginatedTable from "components/Tables/ReactTable/ManualPaginatedTable";
import { ProfileTab } from "enums/ProfileTabs";
import { WorkItemProfiles } from "helpers/profileEnum";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useWorkItems } from "services/input-core/work-items/get-work-items";
import { useSubscription } from "store/auth/auth-store";
import { ProfileEnum } from "utils/@types";
import { getBadgeFromStatus } from "utils/functions";

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
  },
};

const useStyles = makeStyles(styles as never);

export function LatestApprovedAnalysis() {
  const classes = useStyles();
  const { t } = useTranslation("dashboard");
  const { isKuo } = useSubscription();
  const navigate = useNavigate();

  const [tableData, setTableData] = useState({
    pageSize: 10,
    pageIndex: 0,
    columnFilters: [],
    sorting: [],
  });

  const { data, isLoading, isFetching } = useWorkItems({
    "page[size]": tableData.pageSize,
    "page[index]": tableData.pageIndex,
    "filter[approved]": "true",
    "sort[processedAt]": "desc",
  });

  const columns: ColumnDef<any, any>[] = [
    {
      header: t("profile"),
      accessorKey: "profile",
      footer: (props: any) => props?.column?.id,
    },
    {
      header: t("ID"),
      accessorKey: "requestParam",
      footer: (props: any) => props?.column?.id,
    },
    {
      header: t("name"),
      accessorKey: "activityItemName",
      footer: (props: any) => props?.column?.id,
    },
    {
      header: t("activity"),
      accessorKey: "type",
      footer: (props: any) => props?.column?.id,
    },
    {
      header: t("status"),
      accessorKey: "result",
      cell: (info: any) =>
        getBadgeFromStatus(info.getValue(), (text: string) =>
          t(text, { ns: "status" }),
        ),
    },
    {
      header: t("date"),
      accessorKey: "updatedAt",
      cell: (info: any) => new Date(info.getValue()).toLocaleString(),
    },
    {
      header: t("actions"),
      accessorKey: "actions",
      cell: ({ row }: any) => (
        <div className="actions-right">
          <Tooltip placement="top" title="View">
            <ButtonTS
              onClick={() => redirectToProfile(row?.original)}
              round
              justIcon
              size="sm"
              color="info"
            >
              <ArrowForwardIosIcon />
            </ButtonTS>
          </Tooltip>
        </div>
      ),
      enableColumnFilter: false,
    },
  ];

  function redirectToProfile(row: any) {
    const date = new Date(row.activityItemDueDate);
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    if (isKuo && row?.profile !== ProfileEnum.WORKER) {
      return navigate(
        `../${WorkItemProfiles["SOWADM"]}/profile?id=${row.requestParam}&tab=${ProfileTab.ACTIVITY_ITEMS}&month=${month}&year=${year}&activity=${row.type}`,
      );
    }

    navigate(
      `../${WorkItemProfiles[row.profile as never]}/profile?id=${
        row.requestParam
      }&tab=${ProfileTab.ACTIVITY_ITEMS}&month=${month}&year=${year}&activity=${
        row.type
      }`,
    );
  }

  return (
    <>
      <GridItemTS xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <Search />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>{t("last-approved")}</h4>
          </CardHeader>
          <CardBody>
            {isLoading || isFetching ? (
              <Skeleton />
            ) : (
              <PaginatedTable
                columns={columns}
                data={data?.records || []}
                tableState={tableData}
                pageCount={data?.page_count}
                setTableState={setTableData}
                fixedPageSize
                hasFilters={false}
              />
            )}
          </CardBody>
        </Card>
      </GridItemTS>
    </>
  );
}
